import React, { useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { FontFamily } from '../../../constants/fonts';
import { SubmittersMasterLocationData } from './types';
import SkeletonBlock from './SkeletonBlock';

interface MasterLocationTableSectionProps {
  title: string;
  searchable: boolean;
  searchPlaceholder: string;
  rows: SubmittersMasterLocationData[];

  /**
   * When true the table body is replaced by shimmering placeholder rows that
   * mirror the real column layout, so the card keeps its size while loading.
   */
  loading?: boolean;

  /** Number of placeholder rows rendered while loading. */
  skeletonRowCount?: number;
  /** Optional error message to display inside the table when the request failed. */
  error?: string | null;
  /** Called when the user taps the Retry button shown in the error state. */
  onRetry?: () => void;
  /** When true, shows the filter-hint empty message instead of the generic one. */
  hasActiveFilters?: boolean;
}

/**
 * Placeholder rows shown while the table loads.
 *
 * Kept in this file so consumers get the skeleton just by passing `loading`,
 * and so it can reuse the exact column flex / minWidth styles defined below.
 */
const TableSkeleton: React.FC<{ rowCount: number }> = ({ rowCount }) => (
  <>
    {Array.from({ length: rowCount }).map((_, rowIndex) => (
      <View style={styles.row} key={`master-location-skeleton-${rowIndex}`}>
        <View style={[styles.skeletonCell, styles.skeletonLocationCell]}>
          <SkeletonBlock style={styles.skeletonWide} />
        </View>
        <View style={[styles.skeletonCell, styles.skeletonSapIdCell]}>
          <SkeletonBlock style={styles.skeletonNarrow} />
        </View>

        {Array.from({ length: 3 }).map((__, ticketIndex) => (
          <View
            key={`master-location-skeleton-${rowIndex}-ticket-${ticketIndex}`}
            style={[styles.skeletonCell, styles.skeletonTicketCell]}
          >
            <SkeletonBlock style={styles.skeletonNarrow} />
          </View>
        ))}

        <View style={[styles.skeletonCell, styles.skeletonRateCell]}>
          <SkeletonBlock style={styles.skeletonNarrow} />
        </View>
        <View style={[styles.skeletonCell, styles.skeletonAvgTimeCell]}>
          <SkeletonBlock style={styles.skeletonNarrow} />
        </View>
        <View style={[styles.skeletonCell, styles.skeletonTopCategoryCell]}>
          <SkeletonBlock style={styles.skeletonWide} />
        </View>
      </View>
    ))}
  </>
);

const MasterLocationTableSection: React.FC<MasterLocationTableSectionProps> = ({
  title,
  searchable,
  searchPlaceholder,
  rows,
  loading = false,
  skeletonRowCount = 8,
  error = null,
  onRetry,
  hasActiveFilters = false,
}) => {
  const [searchText, setSearchText] = useState('');
  const [sortKey, setSortKey] = useState<'location' | 'sapId' | 'ticketsSubmitted' | 'ticketsClosed' | 'ticketsOpen' | 'resolutionRate' | 'averageResolutionTime' | 'topCategory'>('location');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (
    key:
      | 'location'
      | 'sapId'
      | 'ticketsSubmitted'
      | 'ticketsClosed'
      | 'ticketsOpen'
      | 'resolutionRate'
      | 'averageResolutionTime'
      | 'topCategory'
  ) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(key);
    setSortDirection(key === 'location' || key === 'topCategory' ? 'asc' : 'desc');
  };

  const filteredRows = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) {
      return rows;
    }

    return rows.filter((item) => {
      return (
        item.location.toLowerCase().includes(query) ||
        String(item.sapId).includes(query) ||
        item.topCategoryName.toLowerCase().includes(query)
      );
    });
  }, [rows, searchText]);

  const sortedRows = useMemo(() => {
    const directionMultiplier = sortDirection === 'asc' ? 1 : -1;
    const getComparable = (item: SubmittersMasterLocationData): string | number => {
      if (sortKey === 'topCategory') {
        return item.topCategoryName;
      }

      return item[sortKey];
    };

    return [...filteredRows].sort((a, b) => {
      const left = getComparable(a);
      const right = getComparable(b);

      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * directionMultiplier;
      }

      return String(left).localeCompare(String(right)) * directionMultiplier;
    });
  }, [filteredRows, sortDirection, sortKey]);

  const renderHeader = (
    label: string,
    key:
      | 'location'
      | 'sapId'
      | 'ticketsSubmitted'
      | 'ticketsClosed'
      | 'ticketsOpen'
      | 'resolutionRate'
      | 'averageResolutionTime'
      | 'topCategory',
    style: any,
    isNumeric = false
  ) => (
    <TouchableOpacity onPress={() => handleSort(key)} style={[styles.headerCellWrap, style]}>
      <Text style={[styles.headerCell, isNumeric ? styles.numText : null]}>
        {label}
        {sortKey === key ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}
      </Text>
    </TouchableOpacity>
  );

  return (
    <View style={styles.card}>
      <View style={styles.cardHead}>
        <Text style={styles.cardTitle}>{title}</Text>
        {searchable ? (
          <View style={styles.searchWrap}>
            <Svg width={15} height={15} viewBox="0 0 24 24" style={styles.searchIcon}>
              <Path
                d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
                fill="#818181"
              />
            </Svg>
            <TextInput
              value={searchText}
              onChangeText={setSearchText}
              placeholder={searchPlaceholder}
              placeholderTextColor="#8A8E94"
              style={styles.searchInput}
              // Nothing to filter until the rows arrive.
              editable={!loading}
            />
          </View>
        ) : null}
      </View>

        <View style={styles.tableWrap}>
        {loading ? (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ minWidth: '100%' }}>
          <View style={[styles.table, { minWidth: '100%' }]}>
            <View style={[styles.row, styles.headerRow]}>
              {renderHeader('Location', 'location', styles.locationCell)}
              {renderHeader('SAP ID', 'sapId', styles.sapIdCell, true)}
              {renderHeader('Tickets Submitted', 'ticketsSubmitted', styles.ticketCell, true)}
              {renderHeader('Tickets Closed', 'ticketsClosed', styles.ticketCell, true)}
              {renderHeader('Tickets Open', 'ticketsOpen', styles.ticketCell, true)}
              {renderHeader('Resolution Rate', 'resolutionRate', styles.rateCell, true)}
              {renderHeader('Average Resolution Time', 'averageResolutionTime', styles.avgTimeCell, true)}
              {renderHeader('Top Category', 'topCategory', styles.topCategoryCell)}
            </View>

            <TableSkeleton rowCount={skeletonRowCount} />
          </View>
        </ScrollView>
        ) : sortedRows.length ? (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ minWidth: '100%' }}>
          <View style={[styles.table, { minWidth: '100%' }]}>
            <View style={[styles.row, styles.headerRow]}>
              {renderHeader('Location', 'location', styles.locationCell)}
              {renderHeader('SAP ID', 'sapId', styles.sapIdCell, true)}
              {renderHeader('Tickets Submitted', 'ticketsSubmitted', styles.ticketCell, true)}
              {renderHeader('Tickets Closed', 'ticketsClosed', styles.ticketCell, true)}
              {renderHeader('Tickets Open', 'ticketsOpen', styles.ticketCell, true)}
              {renderHeader('Resolution Rate', 'resolutionRate', styles.rateCell, true)}
              {renderHeader('Average Resolution Time', 'averageResolutionTime', styles.avgTimeCell, true)}
              {renderHeader('Top Category', 'topCategory', styles.topCategoryCell)}
            </View>

            {(loading ? [] : sortedRows).map((item) => (
              <View style={styles.row} key={`${item.location}-${item.sapId}`}>
                <Text style={[styles.cell, styles.locationCell, styles.link]} numberOfLines={1}>
                  {item.location}
                </Text>
                <Text style={[styles.cell, styles.sapIdCell, styles.numText]}>{item.sapId}</Text>
                <Text style={[styles.cell, styles.ticketCell, styles.numText]}>{item.ticketsSubmitted}</Text>
                <Text style={[styles.cell, styles.ticketCell, styles.numText]}>{item.ticketsClosed}</Text>
                <Text style={[styles.cell, styles.ticketCell, styles.numText]}>{item.ticketsOpen}</Text>
                <Text style={[styles.cell, styles.rateCell, styles.numText]}>{item.resolutionRate.toFixed(1)}%</Text>
                <Text style={[styles.cell, styles.avgTimeCell, styles.numText]}>{item.averageResolutionTime.toFixed(1)} hrs</Text>
                <Text style={[styles.cell, styles.topCategoryCell]}>{`${item.topCategoryName} (${item.topCategoryCount})`}</Text>
              </View>
            ))}
          </View>
        </ScrollView>
        ) : error ? (
            <View style={styles.emptyWrap}>
              <Text style={styles.errorText}>{error}</Text>
              {!!onRetry && (
                <TouchableOpacity onPress={onRetry} style={styles.retryButton} accessibilityRole="button" accessibilityLabel="Retry">
                  <Text style={styles.retryText}>↻  Retry</Text>
                </TouchableOpacity>
              )}
            </View>
        ) : (
          <View style={styles.emptyWrap}>
            <Text style={styles.noResults}>{searchText ? `No results found for "${searchText}"` : 'Try expanding your date range or removing some filters.'}</Text>
          </View>
        )}
      </View>

      <Text style={styles.note}>All locations, alphabetical by default. Sort any column or search above.</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E8EDF3',
    padding: 20,
    marginBottom: 16,
  },
  cardHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    marginBottom: 12,
    rowGap: 10,
    columnGap: 12,
  },
  cardTitle: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    color: '#2D2F33',
  },
  searchWrap: {
    width: 260,
    maxWidth: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#D0D5DD',
    borderRadius: 6,
    paddingHorizontal: 12,
    height: 32,
    backgroundColor: '#FFFFFF',
  },
  searchIcon: {
    flexShrink: 0,
  },
  searchInput: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#2D2F33',
    paddingVertical: 0,
    flex: 1,
  },
  tableWrap: {
    overflow: 'hidden',
  },
  table: {
    flex: 1,
    minWidth: '100%',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2F6',
    minHeight: 48,
  },
  headerRow: {
    borderBottomColor: '#DCE3EC',
    borderBottomWidth: 2,
    minHeight: 56,
  },
  headerCellWrap: {
    paddingVertical: 16,
    paddingHorizontal: 12,
    justifyContent: 'center',
  },
  cell: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#2D2F33',
    paddingHorizontal: 12,
    paddingVertical: 12,
    textAlignVertical: 'center',
    includeFontPadding: false,
    height: 48,
  },
  headerCell: {
    fontSize: 11,
    color: '#6A6D71',
    fontFamily: FontFamily.bold,
    textTransform: 'uppercase',
    letterSpacing: 0.2,
    includeFontPadding: false,
  },
  locationCell: {
    flex: 1.2,
    minWidth: 150,
  },
  sapIdCell: {
    flex: 0.8,
    minWidth: 70,
  },
  ticketCell: {
    flex: 1.1,
    minWidth: 140,
  },
  rateCell: {
    flex: 1.1,
    minWidth: 140,
  },
  avgTimeCell: {
    flex: 1.2,
    minWidth: 160,
  },
  topCategoryCell: {
    flex: 1.5,
    minWidth: 160,
  },
  // ─── Loading skeleton ────────────────────────────────────────────────────
  // The `*Cell` styles are shared with Text cells that use `textAlign`, which
  // is invalid on a View, so the skeleton duplicates only flex/minWidth and
  // maps the alignment to `alignItems` so bars sit where the real text does.
  skeletonCell: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    justifyContent: 'center',
  },
  skeletonLocationCell: {
    flex: 1.2,
    minWidth: 150,
    alignItems: 'flex-start',
  },
  skeletonSapIdCell: {
    flex: 0.8,
    minWidth: 70,
    alignItems: 'flex-end',
  },
  skeletonTicketCell: {
    flex: 1.1,
    minWidth: 140,
    alignItems: 'flex-end',
  },
  skeletonRateCell: {
    flex: 1.1,
    minWidth: 140,
    alignItems: 'flex-end',
  },
  skeletonAvgTimeCell: {
    flex: 1.2,
    minWidth: 160,
    alignItems: 'flex-end',
  },
  skeletonTopCategoryCell: {
    flex: 1.5,
    minWidth: 160,
    alignItems: 'flex-start',
  },
  skeletonWide: {
    width: '80%',
    height: 12,
  },
  skeletonNarrow: {
    width: 36,
    height: 12,
  },
  numText: {
    textAlign: 'right',
  },
  link: {
    color: '#005CBC',
    fontFamily: FontFamily.medium,
  },
  note: {
    marginTop: 10,
    fontSize: 12,
    color: '#7A8088',
    fontFamily: FontFamily.regular,
  },
  emptyWrap: {
    minHeight: 160,
    justifyContent: 'center',
    alignItems: 'center',
  },
  noResults: {
    fontSize: 14,
    color: '#7A8088',
    fontFamily: FontFamily.medium,
    textAlign: 'center',
  },
  errorText: {
    fontSize: 14,
    color: '#B42318',
    fontFamily: FontFamily.medium,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 12,
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#1D4ED8',
    alignSelf: 'center',
  },
  retryText: {
    color: '#1D4ED8',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
});

export default MasterLocationTableSection;






